import { useCallback, useEffect, useState } from 'react';
import { listConductRiskSources, unlinkConductRiskSource } from '../functionApi';
import { useAxiosInterceptor } from '../wrappers';
import type {
  ConductRiskSourceLink,
  ConductRiskDocumentType,
} from '../apiTypesConductRisk';

interface UseConductRiskSourcesResult {
  links:    ConductRiskSourceLink[];
  missing:  ConductRiskDocumentType[];
  loading:  boolean;
  error:    string | null;
  refetch:  () => Promise<void>;
  unlink:   (linkId: string) => Promise<void>;
}

export const useConductRiskSources = (
  reportId: string | undefined,
): UseConductRiskSourcesResult => {
  useAxiosInterceptor();
  const [links,   setLinks]   = useState<ConductRiskSourceLink[]>([]);
  const [missing, setMissing] = useState<ConductRiskDocumentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await listConductRiskSources(reportId);
      setLinks(resp.links);
      setMissing(resp.missing_document_types);
    } catch (err: any) {
      setError(err?.message ?? 'Errore nel caricamento delle fonti');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  const unlink = useCallback(async (linkId: string) => {
    if (!reportId) return;
    await unlinkConductRiskSource(reportId, linkId);
    await refetch();
  }, [reportId, refetch]);

  useEffect(() => { refetch(); }, [refetch]);

  return { links, missing, loading, error, refetch, unlink };
};
